import { isCheckoutOrderGuard } from '../typeguards';
import { FunnelParametersType, TransportLegType } from '../analytics';
import { DataLayerEvent, ItemCategory, PurchaseShowPopupAction } from './types';
import {
	BaggageType,
	CheckinOrderFragment,
	CheckinPassengerFragment,
	CheckoutOrderFragment,
	FlightFragment,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsServiceVipSubType,
	NASProductCategory,
	NASProductType,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerFragment,
	RouteType
} from '@websky/graphql';
import { URL_TO_AGENCY_MAP } from '@websky/pixel';

export const getServiceCategory = (
	serviceType: OrderAdditionalServiceGdsServiceServiceType,
	baggageType?: BaggageType
): ItemCategory => {
	if (
		serviceType === OrderAdditionalServiceGdsServiceServiceType.Baggage &&
		(baggageType === BaggageType.PetInHandCarrier ||
			baggageType === BaggageType.PetInHold ||
			baggageType === BaggageType.PetInCabin)
	) {
		return ItemCategory.Pets;
	}

	switch (serviceType) {
		case OrderAdditionalServiceGdsServiceServiceType.Meal:
			return ItemCategory.Meal;

		case OrderAdditionalServiceGdsServiceServiceType.Baggage:
			return ItemCategory.Baggage;

		case OrderAdditionalServiceGdsServiceServiceType.Seat:
			return ItemCategory.Seat;

		case OrderAdditionalServiceGdsServiceServiceType.BusinessLounge:
			return ItemCategory.Business;

		default:
			return null;
	}
};

export const getNasServiceCategory = (category: NASProductCategory, serviceType: NASProductType): ItemCategory => {
	switch (category) {
		case NASProductCategory.vip:
			if (serviceType === NASProductType.Lounge) {
				return ItemCategory.NASVipLounge;
			}

			return null;
		case NASProductCategory.lounge:
			if (serviceType === NASProductType.Lounge) {
				return ItemCategory.NASBusinessLounge;
			}

			return null;
		case NASProductCategory.meet_and_greet:
			return ItemCategory.NASMeetAndGreat;
		default:
			return null;
	}
};

export const getBaggageDatalayerName = (service: GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment) => {
	const splittedWeight = service.baggageWeight?.split(' ') ?? [];
	const [amount, measurement] = splittedWeight.length === 2 ? splittedWeight : [null, null];

	return `${service.name}${amount ? `-${amount}${measurement}` : ''}`;
};

export const animalBaggageTypes = [BaggageType.PetInCabin, BaggageType.PetInHandCarrier, BaggageType.PetInHold];

export const getTravellersTypes = (travellers: PassengerFragment[] | CheckinPassengerFragment[]) => {
	const types = {};

	travellers?.forEach(traveller => {
		if (traveller.type) {
			if (types[traveller.type]) {
				types[traveller.type]++;
			} else {
				types[traveller.type] = 1;
			}
		}
	});

	return Object.entries(types).map(([type, count]) => `${count === 1 ? '' : count}${type}`);
};

const getFunnelDateEnd = (flight: FlightFragment, routeType: RouteType) => {
	if (routeType === RouteType.OW) {
		return null;
	}

	return flight.segmentGroups[flight.segmentGroups.length - 1].segments[0].departure.date;
};

export const getFunnelParameters = (order: CheckoutOrderFragment | CheckinOrderFragment): FunnelParametersType => {
	const isCheckoutOrder = isCheckoutOrderGuard(order);

	const segments = isCheckoutOrder ? order.flight.segments : order.segments;
	const segment = isCheckoutOrder ? order.flight.segments[0].segment : order.segments[0];

	const from = segment.departure;
	const to = segment.arrival;
	const transportLeg = segments.length > 1 ? TransportLegType.Transfer : TransportLegType.Direct;
	const flightNumber = `${segment.operatingAirline?.iata}-${segment.flightNumber}`;
	const routeType = !isCheckoutOrder ? null : order.flight.segmentGroups.length > 1 ? RouteType.RT : RouteType.OW;

	return {
		airport_from: from.airport.iata,
		airport_to: to.airport.iata,
		date_start: from.date,
		date_end: isCheckoutOrder ? getFunnelDateEnd(order.flight, routeType) : null,
		passenger_type: getTravellersTypes(order.travellers),
		route_type: routeType,
		flight_number: flightNumber,
		avia_operator: window.location.hostname,
		transport_leg: transportLeg,
		payment_type: null,
		auth_type: null,
		register_type: null,
		search_id: null
	};
};

export const getAiPurchasePopupGoal = (
	serviceType: OrderAdditionalServiceGdsServiceServiceType,
	action?: PurchaseShowPopupAction
) => {
	const serviceTypeGoal = serviceType ? `_${getServiceCategory(serviceType)}` : '';
	const actionGoal = action ? `_${action}` : '';

	return `${DataLayerEvent.PurchaseShowPopup}${serviceTypeGoal}${actionGoal}`;
};

export const getQueryPopupGoal = (
	serviceType: OrderAdditionalServiceGdsServiceServiceType,
	subType?: GdsServiceVipSubType | BaggageType,
	insuranceCode?: string,
	risc?: string
) => {
	return [DataLayerEvent.QueryShowPopup, risc, serviceType, subType ?? insuranceCode]
		.filter(Boolean)
		.map(v => v.trim().toLowerCase())
		.join('_');
};

export const resolveAgencyByGraphqlUrl = (url: string) => {
	return (
		URL_TO_AGENCY_MAP[url] ?? {
			id: null,
			name: null
		}
	);
};
